import { Card, CardActions, CardContent, CardTagHeader } from '@portals/core/src/components/Card/Card';
import { CategoryLinkList } from '@portals/core/src/components/CategoryLinkList/CategoryLinkList';
import { Pill } from '@portals/core/src/components/Pill/Pill';
import { DEFAULT_MARGIN_TEXT } from '@portals/core/src/components/Spacer/Spacer';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { trackMetaCard } from '@portals/tracking/src/card/trackMetaCard';
import { LinkTracking, TrackingContext } from '@portals/tracking/src/context/tracking';
import React, { useCallback } from 'react';

import { MetaCard as MetaCardModel } from '../../../models';
import { AspectMedia, MEDIA_SIZES as ASPECT_MEDIA_SIZES } from '../AspectMedia/AspectMedia';
import { InternCategoryLink } from '../InternCategoryLink/InternCategoryLink';
import { TopInfoBox } from '../TopInfoBox/TopInfoBox';

interface Props {
  node: MetaCardModel;
}

export const MetaCard: React.FunctionComponent<Props> = ({ node }: Props) => {
  const media = node.image?.media_16x9;

  const handleTracking = useCallback(
    (payload: LinkTracking) => {
      trackMetaCard({
        id: node.id,
        name: node.name,
        fileName: media?.name,
        headline: node.headline?.text,
        ...payload,
      });
    },
    [media?.name, node.headline?.text, node.id, node.name]
  );

  return (
    <TrackingContext.Provider
      value={{
        trackLink: handleTracking,
      }}
    >
      <Card id={`${node.__contentType}.${node.id}`} sx={{ display: 'flex', flexDirection: 'column' }}>
        {media ? (
          <AspectMedia media={media} aspectWidth={16} aspectHeight={9} mediaSizes={ASPECT_MEDIA_SIZES.lg} />
        ) : null}
        <CardContent sx={{ paddingTop: DEFAULT_MARGIN_TEXT }}>
          {node.overline && (
            <Typography variant="h6" component={node.overline.component} color="text.overline" innerPadding="both">
              {node.overline.text}
            </Typography>
          )}
          {node.headline && (
            <Typography variant="h4" component={node.headline.component} innerPadding="both">
              {node.headline.text}
            </Typography>
          )}
          {node.text && (
            <Typography variant="body2" color="text.secondary" innerPadding="both">
              {node.text}
            </Typography>
          )}
          {node.benefits && <TopInfoBox node={node.benefits} />}
        </CardContent>
        <CardTagHeader>
          {node.tags ? node.tags.map((tag) => <Pill key={tag.id} label={tag.name} sx={{ marginRight: 3 }} />) : null}
        </CardTagHeader>
        {node.links?.length ? (
          <CardActions>
            <CategoryLinkList endDivider={false}>
              {node.links.map((element) => {
                return <InternCategoryLink key={element.id} node={element} />;
              })}
            </CategoryLinkList>
          </CardActions>
        ) : null}
      </Card>
    </TrackingContext.Provider>
  );
};
